import { useCallback, useEffect, useRef } from 'react'

/** 一覧の下端からこの距離まで近づいたら次のページを取りに行く */
const LOAD_MORE_MARGIN_PX = 80

type Args = {
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => unknown
  /** 一覧を出し直したとき（件数・表示の切り替え）に監視し直すための値 */
  watchKey: unknown
}

/**
 * スクロールする一覧の下端に近づいたら次のページを読む
 * scrollRef をスクロールする要素に、sentinelRef を一覧の最後の空要素に付ける
 */
export function useLoadMoreOnScroll({ hasNextPage, isFetchingNextPage, fetchNextPage, watchKey }: Args) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return
    void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    const sentinel = sentinelRef.current
    const root = scrollRef.current
    if (!sentinel || !root) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore()
      },
      { root, rootMargin: `${LOAD_MORE_MARGIN_PX}px` },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [loadMore, watchKey])

  return { scrollRef, sentinelRef }
}
